import { Directive, ElementRef, Renderer2, AfterViewInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UUID } from 'angular2-uuid';

/**
 * Directive to enhance screen reader accessibility by appending list item counts
 * to the first found aria-label inside list items. Uses aria-labelledby to link aria-label and list count element.
 *
 * Check if "Convey the number of items in the list" has been improved in future for VoiceOver and TalkBack
 * https://a11ysupport.io/tests/tech__html__ul#assertion-html-ul_element-convey_setsize-
 */
@Directive({
    selector: '[ridangoAriaList]'
})
export class AriaListDirective implements AfterViewInit, OnDestroy {
    private hiddenElements = new Set<HTMLElement>();
    private hiddenContainer: HTMLElement;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private translate: TranslateService
    ) {}

    ngAfterViewInit(): void {
        const liElements: HTMLElement[] = Array.from(this.el.nativeElement.querySelectorAll('li'));
        const total = liElements.length;
        if (!total) return;

        this.hiddenContainer = document.getElementById('ridangoAriaList') || this.renderer.createElement('div');
        if (!this.hiddenContainer.id) {
            this.renderer.setAttribute(this.hiddenContainer, 'id', 'ridangoAriaList');
            this.renderer.setStyle(this.hiddenContainer, 'display', 'none');
            this.renderer.appendChild(document.body, this.hiddenContainer);
        }

        liElements.forEach((li: HTMLElement, index: number) => {
            const count = index + 1;
            const translation = this.translate.instant('LIST_ITEMS_COUNT', { count, total });

            // To avoid ID collisions when multiple lists are present
            const uuid = UUID.UUID();
            const hiddenId = `aria-list-hidden-${uuid}`;
            const targetId = `aria-label-target-${uuid}`;

            const hidden = this.renderer.createElement('span');
            this.renderer.setAttribute(hidden, 'id', hiddenId);
            this.renderer.addClass(hidden, 'cdk-visually-hidden');
            // Part 2 of aria label
            this.renderer.setProperty(hidden, 'innerText', translation);
            this.renderer.appendChild(this.hiddenContainer, hidden);
            this.hiddenElements.add(hidden);

            const target = li.querySelector('[aria-label]') || li.querySelector('button') || li;
            const targetElement = target as HTMLElement;

            if (!targetElement.id) {
                // Part 1 of aria label
                this.renderer.setAttribute(targetElement, 'id', targetId);
            }

            // Part 1 and 2 of aria label
            // Labelledby helps to read dynamic content of part 1 (e.g. translations, async data)
            const labelledby = `${targetElement.id} ${hiddenId}`;
            this.renderer.setAttribute(targetElement, 'aria-labelledby', labelledby);
        });
    }

    ngOnDestroy(): void {
        this.hiddenElements.forEach((hidden) => {
            if (hidden.parentElement === this.hiddenContainer) {
                this.hiddenContainer.removeChild(hidden);
            }
        });
        this.hiddenElements.clear();
    }
}
